import React, { useState, useRef } from "react";
import { api, apiError } from "../lib/api";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { ImageIcon, Wand2, Download, RefreshCw } from "lucide-react";
import { AiBadge } from "./AiBadge";

/**
 * CoverGeneratorCard
 *
 * Self-service AI cover generator for FREE-tier clients.
 *
 * Flow:
 *  1. The client picks an image (screenshot of one of their videos) and
 *     optionally writes a title to overlay.
 *  2. Hits "Genera copertine".
 *  3. The backend (POST /client/cover/generate) calls Nano Banana 3 times
 *     in parallel (Editoriale / Pop / Magazine) so the client can pick
 *     the best one.
 *
 * Quota: 1 generation/day (= 3 variants/day) enforced server-side.
 */
const VARIANT_LABELS = {
  editoriale: "Editoriale",
  pop: "Pop & Bold",
  magazine: "Magazine premium",
};

export default function CoverGeneratorCard() {
  const [file, setFile] = useState(null);
  const [previewSrc, setPreviewSrc] = useState(null);
  const [title, setTitle] = useState("");
  const [variants, setVariants] = useState([]); // [{label, dataUrl}]
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);

  const pickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) {
      toast.error("Carica un'immagine (JPG o PNG)");
      return;
    }
    if (f.size > 8 * 1024 * 1024) {
      toast.error("Immagine troppo grande (max 8 MB)");
      return;
    }
    setFile(f);
    setVariants([]);
    const reader = new FileReader();
    reader.onload = () => setPreviewSrc(reader.result);
    reader.readAsDataURL(f);
  };

  const generate = async () => {
    if (!file) {
      toast.error("Seleziona prima uno screenshot");
      return;
    }
    setLoading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("title", title.trim());
      const { data } = await api.post("/client/cover/generate", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const arr = Array.isArray(data?.images) ? data.images : [];
      if (!arr.length && data?.image_base64) {
        // backward-compat single-image response
        arr.push({
          label: "default",
          image_base64: data.image_base64,
          mime_type: data.mime_type || "image/png",
        });
      }
      if (!arr.length) {
        toast.error("L'AI non ha prodotto immagini. Riprova fra poco.");
        return;
      }
      setVariants(
        arr.map((v) => ({
          label: v.label,
          dataUrl: `data:${v.mime_type || "image/png"};base64,${v.image_base64}`,
        }))
      );
      toast.success(
        `${arr.length} copertine pronte! Scegli la tua preferita e scaricala.`
      );
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  };

  const download = (dataUrl, label) => {
    const a = document.createElement("a");
    a.href = dataUrl;
    a.download = `copertina-${label || "salone"}-${new Date()
      .toISOString()
      .slice(0, 10)}.png`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const reset = () => {
    setFile(null);
    setPreviewSrc(null);
    setTitle("");
    setVariants([]);
    if (inputRef.current) inputRef.current.value = "";
  };

  return (
    <section
      data-testid="client-cover-generator"
      className="rounded-3xl border border-[var(--mp-ink)] bg-white shadow-brutal-sm p-6 sm:p-8 space-y-5"
    >
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="space-y-1">
          <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[var(--mp-muted)] flex items-center gap-2">
            <ImageIcon className="h-3.5 w-3.5" />
            Strumento gratuito · 1 generazione al giorno · 3 stili
          </div>
          <h2 className="font-display text-2xl sm:text-3xl tracking-tighter">
            Genera la copertina del tuo video
          </h2>
        </div>
      </div>

      <p className="text-sm text-[var(--mp-muted)] leading-relaxed max-w-2xl">
        Carica uno screenshot del tuo video e scrivi il titolo che vuoi
        vedere a schermo. L'AI ti propone 3 copertine 9:16 in stili diversi
        — Editoriale, Pop & Bold, Magazine premium — con il soggetto in
        primo piano e lo sfondo attenuato. Scegli quella che preferisci.
      </p>

      {variants.length === 0 && (
        <>
          <div className="space-y-2">
            <label
              htmlFor="cover-title-input"
              className="block text-[10px] font-bold uppercase tracking-[0.22em] text-[var(--mp-muted)]"
            >
              Titolo da scrivere sulla copertina (opzionale)
            </label>
            <input
              id="cover-title-input"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value.slice(0, 80))}
              placeholder='Es. "Taglio nuovo look" oppure "Promo autunno"'
              maxLength={80}
              data-testid="client-cover-title-input"
              className="w-full rounded-xl border border-[var(--mp-ink)] bg-white px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--mp-yellow)]"
            />
            <div className="text-[11px] text-[var(--mp-muted)]">
              Massimo 4 parole, in italiano. Sarà mostrato grande e centrato.
              Se lo lasci vuoto l'AI sceglie un titolo coerente col tuo
              posizionamento.
            </div>
          </div>

          <div className="rounded-2xl border-2 border-dashed border-[var(--mp-border)] bg-[var(--mp-dash-bg)] p-6 grid place-items-center min-h-[160px]">
            {previewSrc ? (
              <img
                src={previewSrc}
                alt="Anteprima"
                data-testid="client-cover-preview-input"
                className="max-h-[280px] w-auto rounded-xl object-contain"
              />
            ) : (
              <div className="text-center text-sm text-[var(--mp-muted)]">
                Nessuna immagine selezionata
              </div>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <input
              ref={inputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={pickFile}
              className="hidden"
              data-testid="client-cover-file-input"
            />
            <Button
              onClick={() => inputRef.current?.click()}
              variant="outline"
              data-testid="client-cover-pick-btn"
              className="rounded-full border-[var(--mp-ink)]"
            >
              <ImageIcon className="h-4 w-4 mr-2" />
              {file ? "Cambia screenshot" : "Scegli screenshot"}
            </Button>
            <Button
              onClick={generate}
              disabled={!file || loading}
              data-testid="client-cover-generate-btn"
              className="rounded-full bg-[var(--mp-ink)] hover:bg-black text-white"
            >
              <Wand2 className="h-4 w-4 mr-2" />
              {loading ? "L'AI sta creando 3 copertine…" : "Genera 3 copertine"}
            </Button>
          </div>
          {loading && (
            <div className="text-[12px] text-[var(--mp-muted)] italic">
              Ci vogliono ~30-60 secondi: l'AI sta creando in parallelo i tre
              stili. Lascia la pagina aperta.
            </div>
          )}
        </>
      )}

      {variants.length > 0 && (
        <>
          <div className="flex items-center gap-2 -mb-2">
            <AiBadge
              label="Copertine generate con AI"
              tooltip="Immagini prodotte da Gemini Nano Banana. Rileggile prima di pubblicarle — controlla che siano coerenti col tuo brand."
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {variants.map((v, idx) => (
              <div
                key={v.label || idx}
                data-testid={`client-cover-variant-${v.label || idx}`}
                className="rounded-2xl border border-[var(--mp-ink)] bg-[var(--mp-dash-bg)] p-3 flex flex-col gap-3"
              >
                <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[var(--mp-muted)]">
                  Stile {idx + 1} · {VARIANT_LABELS[v.label] || v.label}
                </div>
                <div className="rounded-xl bg-white grid place-items-center overflow-hidden">
                  <img
                    src={v.dataUrl}
                    alt={`Copertina ${VARIANT_LABELS[v.label] || v.label}`}
                    data-testid={`client-cover-result-${v.label || idx}`}
                    className="w-full h-auto object-contain max-h-[420px]"
                  />
                </div>
                <Button
                  onClick={() => download(v.dataUrl, v.label)}
                  data-testid={`client-cover-download-${v.label || idx}-btn`}
                  className="rounded-full bg-[var(--mp-ink)] hover:bg-black text-white"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Scarica
                </Button>
              </div>
            ))}
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              onClick={reset}
              variant="outline"
              data-testid="client-cover-reset-btn"
              className="rounded-full border-[var(--mp-ink)]"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Ricomincia
            </Button>
          </div>
          <div className="text-[11px] text-[var(--mp-muted)]">
            Hai usato la generazione di oggi (3 stili). Torna domani per
            generarne altre 3 gratis, oppure passa al piano Pro per
            copertine illimitate fatte dal nostro team.
          </div>
        </>
      )}
    </section>
  );
}
